import api, { ApiResponse } from './api/apiClient';

export interface AdSummary {
  total_spend: number;
  total_impressions: number;
  total_clicks: number;
  total_purchases: number;
  total_purchase_value: number;
  avg_roas: number;
  by_platform: Record<string, { spend: number; purchases: number; purchase_value: number }>;
}

export interface AdDailyData {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  real_revenue?: number;
  real_revenue_by_channel?: Record<string, number>;
}

export interface AdBreakdownRow {
  platform: string;
  campaign_id: string;
  campaign_name: string;
  adset_id?: string;
  adset_name?: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  ctr: number;
  reach: number;
  unique_link_clicks: number;
}

export interface RealRoasData {
  total_spend: number;
  total_revenue: number;
  real_roas: number;
  channel_revenues: { channel: string; revenue: number }[];
}

export interface AdAccount {
  id: string;
  platform: string;
  account_id: string;
  account_name: string;
  is_active: boolean;
  last_sync_at: string;
  created_at: string;
  has_credentials: boolean;
}

export interface CampaignItem {
  campaign_id: string;
  campaign_name: string;
  platform: string;
}

export interface CampaignChannelMapping {
  id: string;
  campaign_id: string;
  platform: string;
  campaign_name: string;
  /** 표시·하위호환용. 집계 정본은 sales_channel_id 다. */
  channel_name: string;
  /**
   * 판매채널 UUID (집계 정본). 서버가 채널명을 해석해 항상 함께 저장하므로
   * 신규·기존 매핑 모두 값이 채워진다. 구버전 코드가 남긴 행만 일시적으로 null 일 수 있다.
   */
  sales_channel_id?: string | null;
}

/** 미매핑 캠페인 수 (경량, 사이드바·페이지 공용) */
export interface UnmappedCountData {
  count: number;
  window_days: number;
}

/** 미매핑 캠페인 미리보기 항목 (페이지 배너 전용) */
export interface UnmappedPreviewItem {
  platform: string;
  campaign_id: string;
  campaign_name: string;
}

/** 미매핑 캠페인 미리보기 (count + 상위 N건 캠페인명) */
export interface UnmappedPreviewData {
  count: number;
  window_days: number;
  items: UnmappedPreviewItem[];
}

export interface ChannelRoasData {
  by_channel: Array<{
    channel: string;
    revenue: number;
    total_spend: number;
    roas: number;
    by_platform: Array<{ platform: string; spend: number; campaign_count: number }>;
  }>;
}

export interface AdInsight {
  type: 'platform' | 'campaign' | 'creative' | 'funnel' | 'channel' | 'efficiency' | 'volatility' | 'attribution' | 'season' | 'event' | 'trend' | 'action' | 'info' | string;
  icon: string;
  title: string;
  message: string;
}

export interface AdDailyResponse {
  daily: AdDailyData[];
  channel_names: string[];
}

export interface AdInsightsData {
  insights: AdInsight[];
}

export interface AdFilterParams {
  platforms?: string;
  channel_names?: string;
  start_date?: string;
  end_date?: string;
  level?: 'campaign' | 'adset';
  campaign_id?: string;
}

const BASE = '/marketing/ads';

export const advertisingService = {
  async getSummary(params: AdFilterParams): Promise<AdSummary> {
    const res = await api.get<ApiResponse<AdSummary>>(`${BASE}/summary`, { params });
    return res.data!;
  },

  async getDailyTrend(params: AdFilterParams): Promise<AdDailyResponse> {
    const res = await api.get<ApiResponse<AdDailyData[]> & { channel_names?: string[] }>(`${BASE}/daily`, { params });
    return { daily: res.data || [], channel_names: (res as any).channel_names || [] };
  },

  async getBreakdown(params: AdFilterParams): Promise<AdBreakdownRow[]> {
    const res = await api.get<ApiResponse<AdBreakdownRow[]>>(`${BASE}/breakdown`, { params });
    return res.data!;
  },

  async getRealRoas(params: { start_date?: string; end_date?: string }): Promise<RealRoasData> {
    const res = await api.get<ApiResponse<RealRoasData>>(`${BASE}/real-roas`, { params });
    return res.data!;
  },

  async getAccounts(): Promise<AdAccount[]> {
    const res = await api.get<ApiResponse<AdAccount[]>>(`${BASE}/accounts`);
    return res.data!;
  },

  async createAccount(payload: {
    platform: string;
    account_id: string;
    account_name?: string;
    credentials: object;
  }): Promise<AdAccount> {
    const res = await api.post<ApiResponse<AdAccount>>(`${BASE}/accounts`, payload);
    return res.data!;
  },

  async updateAccount(id: string, payload: {
    account_name?: string;
    is_active?: boolean;
    credentials?: object;
  }): Promise<AdAccount> {
    const res = await api.put<ApiResponse<AdAccount>>(`${BASE}/accounts/${id}`, payload);
    return res.data!;
  },

  async deleteAccount(id: string): Promise<void> {
    await api.delete<void>(`${BASE}/accounts/${id}`);
  },

  async syncManually(daysAgo = 1): Promise<{ success: number; failed: number }> {
    const res = await api.post<ApiResponse<{ success: number; failed: number }>>(`${BASE}/sync`, { days_ago: daysAgo });
    return res.data!;
  },

  async getCampaigns(): Promise<CampaignItem[]> {
    const res = await api.get<ApiResponse<CampaignItem[]>>(`${BASE}/campaigns`);
    return res.data!;
  },

  async getCampaignChannels(): Promise<CampaignChannelMapping[]> {
    const res = await api.get<ApiResponse<CampaignChannelMapping[]>>(`${BASE}/campaign-channels`);
    return res.data!;
  },

  async addCampaignChannel(payload: Omit<CampaignChannelMapping, 'id'>): Promise<CampaignChannelMapping> {
    const res = await api.post<ApiResponse<CampaignChannelMapping>>(`${BASE}/campaign-channels`, payload);
    return res.data!;
  },

  async deleteCampaignChannel(id: string): Promise<void> {
    await api.delete<void>(`${BASE}/campaign-channels/${id}`);
  },

  /** 미매핑 캠페인 수 조회 (경량) — 사이드바 배지·페이지 배너 공용 */
  async getUnmappedCount(): Promise<UnmappedCountData> {
    const res = await api.get<ApiResponse<UnmappedCountData>>(`${BASE}/campaign-channels/unmapped-count`);
    return res.data!;
  },

  /** 미매핑 캠페인 미리보기 (count + 상위 N건 캠페인명) — 페이지 배너 전용 */
  async getUnmappedPreview(): Promise<UnmappedPreviewData> {
    const res = await api.get<ApiResponse<UnmappedPreviewData>>(`${BASE}/campaign-channels/unmapped-preview`);
    return res.data!;
  },

  /**
   * 캠페인 채널 집합 원자적 교체 (edit) — 서버 단일 요청 [C-12].
   * 기존 "전체 삭제 후 재등록" 다중 호출 패턴을 대체한다.
   */
  async replaceCampaignChannels(payload: {
    platform: string;
    campaign_id: string;
    campaign_name?: string;
    channel_names: string[];
  }): Promise<{ platform: string; campaign_id: string; channel_names: string[] }> {
    const res = await api.put<ApiResponse<{ platform: string; campaign_id: string; channel_names: string[] }>>(
      `${BASE}/campaign-channels/replace`,
      payload,
    );
    return res.data!;
  },

  /**
   * (platform, campaign_id) 그룹 전체 삭제 (delete) — 서버 단일 요청 [C-25].
   * 기존 id별 다중 DELETE 패턴을 대체한다. DELETE body 로 파라미터 전달.
   */
  async deleteCampaignGroup(payload: { platform: string; campaign_id: string }): Promise<void> {
    await api.delete<ApiResponse<{ platform: string; campaign_id: string }>>(
      `${BASE}/campaign-channels/group`,
      { data: payload },
    );
  },

  async getChannelRoas(params: { start_date?: string; end_date?: string; platforms?: string; channel_names?: string }): Promise<ChannelRoasData> {
    const res = await api.get<ApiResponse<ChannelRoasData>>(`${BASE}/channel-roas`, { params });
    return res.data!;
  },

  async getAdInsights(params: { start_date?: string; end_date?: string }): Promise<AdInsightsData> {
    const res = await api.get<ApiResponse<AdInsightsData>>(`${BASE}/insights`, { params });
    return res.data!;
  },

};
